/* Multicolumn slider enhancement.
   Wires pill-dot indicators, syncs an .is-active class on the current
   slide, and runs a one-shot scroll teaser the first time the slider
   enters the viewport so users see the swipe gesture is available.
   Leans on the existing <slider-component> for prev/next + scroll snap;
   only adds DOM and listeners — never replaces the underlying control. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TEASER_SESSION_KEY = 'azan-multicolumn-teaser';

  const enhance = (root) => {
    if (!root || root.dataset.azanEnhanced === '1') return;
    const slider = root.querySelector('.slider');
    if (!slider || !slider.classList.contains('slider--tablet')) return;

    const slides = Array.from(slider.querySelectorAll('.slider__slide'));
    if (slides.length < 2) return;

    const buttonsRow = root.querySelector('.slider-buttons');
    if (!buttonsRow) return;

    root.dataset.azanEnhanced = '1';

    /* NOTE: swipe-to-navigate used to be emulated here with
       touchstart/touchend handlers, because the section set
       `touch-action: pan-y` and thereby disabled native horizontal
       panning. That combination trapped vertical swipes on iOS (see the
       comment in sections/multicolumn.liquid) and the emulation only
       fired on touchend, so it had no momentum and felt unresponsive.

       The section now uses `touch-action: auto` and lets the browser
       disambiguate the gesture natively — the same thing the
       featured-collection slider on this page has always done, which is
       why that one always felt right. The handlers are gone rather than
       merely disabled: leaving them in alongside native scrolling made
       one swipe advance two slides.

       scrollToSlide is kept — the dot indicators below still use it. */
    const scrollToSlide = (idx) => {
      const target = slides[Math.max(0, Math.min(slides.length - 1, idx))];
      if (!target) return;
      slider.scrollTo({
        left: target.offsetLeft - slider.offsetLeft,
        behavior: 'smooth',
      });
    };
    const dotsWrap = document.createElement('div');
    dotsWrap.className = 'azan-multicolumn-dots';
    dotsWrap.setAttribute('role', 'tablist');
    const sliderPositionLabel = root.dataset.azanSliderPositionLabel || 'Slider position';
    const goToSlideLabel = root.dataset.azanGoToSlideLabel || 'Go to slide [number]';
    dotsWrap.setAttribute('aria-label', sliderPositionLabel);

    const dots = slides.map((slide, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'azan-multicolumn-dot';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', goToSlideLabel.replace('[number]', String(i + 1)));
      if (i === 0) btn.setAttribute('aria-current', 'true');
      btn.addEventListener('click', () => {
        slider.scrollTo({
          left: slide.offsetLeft - slider.offsetLeft,
          behavior: 'smooth',
        });
      });
      dotsWrap.appendChild(btn);
      return btn;
    });

    const counter = buttonsRow.querySelector('.slider-counter');
    const nextBtn = buttonsRow.querySelector('.slider-button--next');
    if (counter) counter.replaceWith(dotsWrap);
    else if (nextBtn) buttonsRow.insertBefore(dotsWrap, nextBtn);
    else buttonsRow.appendChild(dotsWrap);

    const hint = document.createElement('span');
    hint.className = 'azan-multicolumn-hint';
    hint.textContent = root.dataset.azanSwipeHint || 'Swipe';
    buttonsRow.insertAdjacentElement('afterend', hint);

    const setActive = (idx) => {
      if (idx < 0 || idx >= slides.length) return;
      slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
      dots.forEach((d, i) => {
        if (i === idx) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
        d.classList.toggle('is-past', i < idx);
        d.classList.toggle('is-future', i > idx);
      });
    };
    setActive(0);

    /* Prefer the slider-component's slideChanged event so we stay in
       sync with its own page bookkeeping (used by prev/next clicks). */
    root.addEventListener('slideChanged', (e) => {
      const page = e.detail && e.detail.currentPage;
      if (typeof page === 'number') setActive(page - 1);
    });

    /* Fallback: derive active slide from scroll position. Throttled
       with rAF so it stays cheap during inertial scroll. */
    let ticking = false;
    const updateFromScroll = () => {
      ticking = false;
      const sRect = slider.getBoundingClientRect();
      const centerX = sRect.left + sRect.width / 2;
      let bestIdx = 0;
      let bestDist = Infinity;
      slides.forEach((s, i) => {
        const r = s.getBoundingClientRect();
        const c = r.left + r.width / 2;
        const d = Math.abs(c - centerX);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      });
      setActive(bestIdx);
    };
    slider.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateFromScroll);
    }, { passive: true });

    /* Peek: render the next card before it is swiped in (phones and
       tablets only, i.e. while the list is a horizontal scroller; see
       component-slider.css, max-width: 989px). Desktop is a plain grid
       and keeps its per-card behaviour.

       (1) Reveal. azan-motion.js gives every .multicolumn-card its own
       scroll reveal (opacity 0 + translateY until its viewport
       IntersectionObserver adds .is-visible). Inside this scroller the
       cards right of the current one are clipped sideways: the peeking
       card shows ~10% of its width (below that observer's 12%
       threshold), the rest 0%. So they stayed invisible until dragged
       in and the peek strip was empty on load. Fix: as soon as any
       card of this slider is revealed, reveal all of them in the same
       frame, so the peeking card fades in together with the first one.

       (2) Images. image_tag emits loading="lazy", and a lazy image in
       a clipped horizontal scroller is only fetched once it is (almost)
       swiped into view (Chrome: ~500px ahead; WebKit possibly not
       before it is visible). Once the section approaches the viewport,
       switch the slide photos to eager so each one is loaded before its
       card can peek. Not eager from page load: no extra bytes for
       visitors who never scroll this far.

       Each block detaches its observer and its media-query listener as
       soon as it has nothing left to do, at any width: reveal once no
       card is waiting for azan-motion (all revealed, or none hidden in
       the first place, e.g. reduced motion), images once all slide
       photos have loaded. */
    const sliderMq = window.matchMedia('(max-width: 989px)');
    const onSliderMqChange = (fn) => {
      if (sliderMq.addEventListener) {
        sliderMq.addEventListener('change', fn);
        return () => sliderMq.removeEventListener('change', fn);
      }
      if (sliderMq.addListener) {
        sliderMq.addListener(fn);
        return () => sliderMq.removeListener(fn);
      }
      return () => {};
    };

    const cards = Array.from(slider.querySelectorAll('.multicolumn-card'));
    if (cards.length > 1 && 'MutationObserver' in window) {
      /* Hidden by azan-motion.js and not revealed yet. azan-motion.js
         is a deferred script in <head>, so it has tagged the cards
         before this deferred section script runs. */
      const isPending = (c) => c.classList.contains('azan-reveal') && !c.classList.contains('is-visible');
      let done = false;
      let stopMq = null;
      let revealMo = null;
      const finish = () => {
        done = true;
        revealMo.disconnect();
        if (stopMq) stopMq();
      };
      const syncCards = () => {
        if (done) return;
        if (!cards.some(isPending)) {
          finish();
          return;
        }
        if (!sliderMq.matches || !cards.some((c) => c.classList.contains('is-visible'))) return;
        /* Only cards azan-motion.js has hidden; never re-add an
           existing class (that would queue another mutation). */
        cards.forEach((c) => {
          if (isPending(c)) c.classList.add('is-visible');
        });
        finish();
      };
      revealMo = new MutationObserver(syncCards);
      cards.forEach((c) => revealMo.observe(c, { attributes: true, attributeFilter: ['class'] }));
      stopMq = onSliderMqChange(syncCards);
      syncCards();
    }

    const lazyImgs = Array.from(slider.querySelectorAll('img[loading="lazy"]'));
    if (lazyImgs.length && 'IntersectionObserver' in window) {
      /* naturalWidth as well: a deferred lazy image must never count as
         loaded, whatever an engine reports for .complete. */
      const isLoaded = (img) => img.complete && img.naturalWidth > 0;
      let done = false;
      let sectionNear = false;
      let stopMq = null;
      let imgIo = null;
      const finish = () => {
        done = true;
        imgIo.disconnect();
        if (stopMq) stopMq();
        lazyImgs.forEach((img) => img.removeEventListener('load', syncImages));
      };
      const syncImages = () => {
        if (done) return;
        if (lazyImgs.every(isLoaded)) {
          finish();
          return;
        }
        if (!sectionNear || !sliderMq.matches) return;
        lazyImgs.forEach((img) => img.setAttribute('loading', 'eager'));
        finish();
      };
      imgIo = new IntersectionObserver((entries) => {
        sectionNear = entries[entries.length - 1].isIntersecting;
        syncImages();
      }, { rootMargin: '1250px 0px 1250px 0px' });
      imgIo.observe(slider);
      stopMq = onSliderMqChange(syncImages);
      /* Desktop loads them natively; stop watching once they are in. */
      lazyImgs.forEach((img) => img.addEventListener('load', syncImages));
    }

    const fadeHint = () => hint.classList.add('is-faded');

    if (reduceMotion.matches) {
      fadeHint();
      return;
    }

    /* One-shot scroll teaser: nudges the slider ~80px right then back
       to zero on first appearance, so the gesture is obvious. Stored
       per session so it doesn't fire on every page navigation. */
    const shouldTeaser = () => {
      try {
        return !sessionStorage.getItem(TEASER_SESSION_KEY);
      } catch (e) {
        return true;
      }
    };

    const markTeaserDone = () => {
      try {
        sessionStorage.setItem(TEASER_SESSION_KEY, '1');
      } catch (e) {
        /* ignore */
      }
    };

    if (!shouldTeaser()) {
      setTimeout(fadeHint, 4200);
      return;
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        setTimeout(() => {
          const max = slider.scrollWidth - slider.clientWidth;
          if (max <= 0) {
            fadeHint();
            return;
          }
          const teaser = Math.min(72, max);
          slider.scrollTo({ left: teaser, behavior: 'smooth' });
          setTimeout(() => {
            slider.scrollTo({ left: 0, behavior: 'smooth' });
            markTeaserDone();
            setTimeout(fadeHint, 2600);
          }, 720);
        }, 900);
      });
    }, { threshold: 0.45 });
    io.observe(slider);
  };

  const scanAll = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.multicolumn slider-component').forEach(enhance);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scanAll());
  } else {
    scanAll();
  }

  /* Theme editor reload + AJAX-injected sections. */
  document.addEventListener('shopify:section:load', (e) => scanAll(e.target));
})();
